/**
 * NOTIF-008 — the words of the "record not found / deleted" modal a dead notification link
 * shows: what the record was (the server's label), that it is gone, and — for a real soft
 * delete — since when and by whom. NOTIF-011: an export whose download window closed —
 * "Export expired on Oct 30, 4:15 PM" (owner, 2026-10-29) — or whose file the server refused
 * for a reason of its own, in the server's words (owner, 2026-11-01).
 */
import type { NotificationTarget } from '@/services/notificationsApi'
import { formatExpiresAt } from '@/lib/notifications/exports'

export interface DeadLinkCopy {
  title: string
  message: string
}

export type DeadLinkTarget = Pick<
  NotificationTarget,
  'state' | 'entity' | 'label' | 'deleted_at' | 'deleted_by' | 'reason'
>

/** "Oct 27, 2026" in the viewer's locale conventions; null when the timestamp is unreadable. */
export function formatDeletedDay(iso: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

export function deadLinkCopy(target: DeadLinkTarget): DeadLinkCopy {
  if (target.entity === 'notification') {
    return { title: 'Notification not found', message: 'This notification no longer exists.' }
  }
  const label = target.label ?? 'This record'
  if (target.state === 'expired') {
    // The server's own sentence when it refused the file ("Export expired on …", "The export
    // file is no longer on the server."): a date from the payload could be made up.
    const reason = target.reason?.trim().replace(/\.+$/, '')
    if (reason) {
      return {
        title: 'Export expired',
        message: `${reason}. Start it again from its page to get a fresh file.`
      }
    }
    const when = target.deleted_at ? formatExpiresAt(target.deleted_at) : null
    return {
      title: 'Export expired',
      message: when
        ? `${label} expired on ${when}. Start it again from its page to get a fresh file.`
        : `${label} has expired. Start it again from its page to get a fresh file.`
    }
  }
  if (target.state === 'deleted') {
    const day = target.deleted_at ? formatDeletedDay(target.deleted_at) : null
    const by = target.deleted_by ? ` by ${target.deleted_by}` : ''
    return {
      title: 'Record deleted',
      message: day ? `${label} was deleted on ${day}${by}.` : `${label} was deleted${by}.`
    }
  }
  return {
    title: 'Record not found',
    message: `${label} could not be found — it may have been deleted.`
  }
}
